import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { differenceInSeconds } from 'date-fns';
import { countdownParts } from '../../../../../utils/countdownParts';

type CountdownTimerProps = {
  nextGiveaway: Date;
  isTiny?: boolean;
  isJumbo?: boolean;
  fieldsToShow?: Array<'days' | 'hours' | 'minutes' | 'seconds'>;
};

function CountdownTimer({
  nextGiveaway,
  isTiny = false,
  isJumbo = false,
  fieldsToShow = ['days', 'hours', 'minutes', 'seconds'] // Default to all fields
}: CountdownTimerProps) {
  const [countdownTimer, setCountdownTimer] = useState<number>(
    differenceInSeconds(nextGiveaway, new Date())
  );

  const zeroPad = (num: number) => String(num).padStart(2, '0');
  const duration = countdownParts(countdownTimer);

  // Recalculate countdown when nextGiveaway changes
  useEffect(() => {
    const now = new Date();
    const newCountdown = differenceInSeconds(nextGiveaway, now);

    // Ensure we don't set negative countdown
    setCountdownTimer(Math.max(0, newCountdown));
  }, [nextGiveaway]);

  useEffect(() => {
    if (countdownTimer > 0) {
      const timeout = setTimeout(() => {
        setCountdownTimer(countdownTimer - 1);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [countdownTimer]);

  const renderField = (label: string, value: number | undefined) => (
    <Box
      sx={{
        // isTiny (dashboard): let the four boxes share the row equally so they
        // stay on one line; other sizes keep their fixed widths. isJumbo (the
        // giveaway page) does the same on a phone: at a fixed 108px, four
        // fields and their gaps came to 461px, so on every phone the countdown
        // broke into two rows of two. From sm up, see the end of this block.
        flex: isTiny
          ? '1 1 0'
          : isJumbo
            ? { xs: '1 1 0', sm: '0 1 auto' }
            : undefined,
        minWidth: isJumbo ? { xs: 0, sm: 150 } : isTiny ? 0 : 104,
        px: isJumbo ? { xs: 0.5, sm: 2.8 } : isTiny ? { xs: 0.8, sm: 1.4 } : 2,
        py: isJumbo
          ? { xs: 1.7, sm: 2.4 }
          : isTiny
            ? { xs: 0.85, sm: 1.1 }
            : 1.5,
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.16)',
        textAlign: 'center',
        backdropFilter: 'blur(6px)',
        // From sm up the jumbo fields keep their 150px wherever four of them
        // fit (648px with the gaps). Where they don't — a phone held sideways,
        // a tablet, a laptop beside the sidebar — they wrapped into two rows of
        // two; now the whole countdown scales down with its row instead. The
        // sizes are container units of the row, each the full-size value's
        // share of 648px (150px = 23.148%, 22.4px = 3.457%, 19.2px = 2.963%),
        // so it shrinks in proportion. Browsers without container units keep
        // the wrapping.
        ...(isJumbo && {
          '@supports (width: 1cqi)': {
            flex: { sm: '0 1 150px' },
            minWidth: { sm: 0 },
            px: { sm: 'min(22.4px, 3.457cqi)' },
            py: { sm: 'min(19.2px, 2.963cqi)' }
          }
        })
      }}
    >
      <Typography
        textAlign="center"
        sx={{
          // jumbo digits scale with the phone so "00" fits a quarter-width field
          fontSize: isJumbo
            ? { xs: 'clamp(26px, 9vw, 52px)', sm: 70 }
            : isTiny
              ? { xs: 24, sm: 34 }
              : { xs: 34, sm: 42 },
          lineHeight: 1,
          fontWeight: 900,
          color: '#ffffff',
          fontVariantNumeric: 'tabular-nums',
          // sm+: 70px = 10.802% of the 648px row (see the field)
          ...(isJumbo && {
            '@supports (width: 1cqi)': {
              fontSize: { sm: 'min(70px, 10.802cqi)' }
            }
          })
        }}
      >
        {zeroPad(value ?? 0)}
      </Typography>
      <Typography
        textAlign="center"
        sx={{
          mt: 0.7,
          fontSize: isJumbo
            ? { xs: 'clamp(10px, 3.1vw, 13.5px)', sm: 16.5 }
            : isTiny
              ? { xs: 10.5, sm: 12 }
              : 13,
          lineHeight: 1,
          fontWeight: 900,
          letterSpacing:
            isTiny || isJumbo ? { xs: '0.08em', sm: '0.12em' } : '0.12em',
          color: 'rgba(255,255,255,0.78)',
          // sm+: 16.5px = 2.546% of the 648px row (see the field)
          ...(isJumbo && {
            '@supports (width: 1cqi)': {
              fontSize: { sm: 'min(16.5px, 2.546cqi)' }
            }
          })
        }}
      >
        {label.toUpperCase()}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        flexWrap: isTiny
          ? 'nowrap'
          : isJumbo
            ? { xs: 'nowrap', sm: 'wrap' }
            : 'wrap',
        gap: isJumbo ? { xs: 1.2, sm: 2 } : isTiny ? { xs: 0.8, sm: 1.4 } : 2,
        // sm+: the row is the container the jumbo fields size themselves
        // against; it stays on one line and its 16px gaps tighten with it
        // (2.469% of 648px)
        ...(isJumbo && {
          '@supports (width: 1cqi)': {
            containerType: { sm: 'inline-size' },
            flexWrap: { sm: 'nowrap' },
            columnGap: { sm: 'min(16px, 2.469%)' }
          }
        })
      }}
    >
      {fieldsToShow.includes('days') && renderField('Days', duration.days)}
      {fieldsToShow.includes('hours') && renderField('Hours', duration.hours)}
      {fieldsToShow.includes('minutes') &&
        renderField('Mins', duration.minutes)}
      {fieldsToShow.includes('seconds') &&
        renderField('Secs', duration.seconds)}
    </Box>
  );
}

export default CountdownTimer;
